/**
 * Comprehensive Monitoring Service
 * Provides error tracking, performance monitoring, and health checks
 */

export interface ErrorEvent {
  id: string;
  timestamp: Date;
  level: 'error' | 'warning' | 'info';
  message: string;
  stack?: string;
  context?: Record<string, any>;
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
}

export interface PerformanceMetric {
  id: string;
  timestamp: Date;
  name: string;
  value: number;
  unit: string;
  context?: Record<string, any>;
}

export interface HealthCheckResult {
  service: string;
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: Date;
  responseTime?: number;
  details?: Record<string, any>;
  error?: string;
}

class MonitoringService {
  private errorBuffer: ErrorEvent[] = [];
  private performanceBuffer: PerformanceMetric[] = [];
  private healthChecks: Map<string, HealthCheckResult> = new Map();
  private sessionId: string;
  private isEnabled: boolean = true;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initializeMonitoring();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeMonitoring(): void {
    // Global error handler
    window.addEventListener('error', (event) => {
      this.trackError({
        level: 'error',
        message: event.message,
        stack: event.error?.stack,
        context: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError({
        level: 'error',
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stack: event.reason?.stack,
        context: {
          type: 'unhandledrejection',
          reason: event.reason,
        },
      });
    });

    // Performance observer for navigation timing
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            this.trackPerformance({
              name: entry.name,
              value: entry.duration || entry.startTime,
              unit: 'ms',
              context: {
                entryType: entry.entryType,
                startTime: entry.startTime,
              },
            });
          });
        });
        
        observer.observe({ entryTypes: ['navigation', 'measure', 'mark'] });
      } catch (_error) {
        console.warn('Performance Observer not supported:', _error);
      }
    }

    // Start periodic health checks
    this.startHealthChecks();
  }

  /**
   * Track an error event
   */
  trackError(error: Partial<ErrorEvent>): void {
    if (!this.isEnabled) return;

    const errorEvent: ErrorEvent = {
      id: this.generateId(),
      timestamp: new Date(),
      level: error.level || 'error',
      message: error.message || 'Unknown error',
      stack: error.stack,
      context: error.context,
      userId: this.getCurrentUserId(),
      sessionId: this.sessionId,
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...error,
    };

    this.errorBuffer.push(errorEvent);
    this.processErrorEvent(errorEvent);

    // Keep buffer size manageable
    if (this.errorBuffer.length > 1000) {
      this.errorBuffer = this.errorBuffer.slice(-500);
    }
  }

  /**
   * Track a performance metric
   */
  trackPerformance(metric: Partial<PerformanceMetric>): void {
    if (!this.isEnabled) return;

    const performanceMetric: PerformanceMetric = {
      id: this.generateId(),
      timestamp: new Date(),
      name: metric.name || 'unknown_metric',
      value: metric.value || 0,
      unit: metric.unit || 'ms',
      context: metric.context,
      ...metric,
    };

    this.performanceBuffer.push(performanceMetric);

    // Keep buffer size manageable
    if (this.performanceBuffer.length > 1000) {
      this.performanceBuffer = this.performanceBuffer.slice(-500);
    }

    // Check for performance alerts
    this.checkPerformanceThresholds(performanceMetric);
  }

  /**
   * Record a health check result
   */
  recordHealthCheck(result: Partial<HealthCheckResult>): void {
    const healthCheck: HealthCheckResult = {
      service: result.service || 'unknown',
      status: result.status || 'unhealthy',
      timestamp: new Date(),
      responseTime: result.responseTime,
      details: result.details,
      error: result.error,
      ...result,
    };

    this.healthChecks.set(healthCheck.service, healthCheck);

    // Alert on unhealthy services
    if (healthCheck.status === 'unhealthy') {
      this.trackError({
        level: 'error',
        message: `Health check failed for ${healthCheck.service}`,
        context: {
          service: healthCheck.service,
          error: healthCheck.error,
          details: healthCheck.details,
        },
      });
    }
  }

  /**
   * Get current system health status
   */
  getHealthStatus(): {
    overall: 'healthy' | 'degraded' | 'unhealthy';
    services: HealthCheckResult[];
    lastUpdated: Date;
  } {
    const services = Array.from(this.healthChecks.values());
    const unhealthyCount = services.filter(s => s.status === 'unhealthy').length;
    const degradedCount = services.filter(s => s.status === 'degraded').length;

    let overall: 'healthy' | 'degraded' | 'unhealthy' = 'healthy';
    if (unhealthyCount > 0) {
      overall = 'unhealthy';
    } else if (degradedCount > 0) {
      overall = 'degraded';
    }

    return {
      overall,
      services,
      lastUpdated: new Date(),
    };
  }

  /**
   * Get error statistics
   */
  getErrorStats(timeWindow: number = 3600000): {
    total: number;
    byLevel: Record<string, number>;
    recent: ErrorEvent[];
    topErrors: Array<{ message: string; count: number }>;
  } {
    const cutoff = new Date(Date.now() - timeWindow);
    const recentErrors = this.errorBuffer.filter(e => e.timestamp >= cutoff);

    const byLevel = recentErrors.reduce((acc, error) => {
      acc[error.level] = (acc[error.level] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const errorCounts = recentErrors.reduce((acc, error) => {
      acc[error.message] = (acc[error.message] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topErrors = Object.entries(errorCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([message, count]) => ({ message, count }));

    return {
      total: recentErrors.length,
      byLevel,
      recent: recentErrors.slice(-50),
      topErrors,
    };
  }

  /**
   * Get performance statistics
   */
  getPerformanceStats(timeWindow: number = 3600000): {
    metrics: PerformanceMetric[];
    averages: Record<string, number>;
    slowest: PerformanceMetric[];
  } {
    const cutoff = new Date(Date.now() - timeWindow);
    const recentMetrics = this.performanceBuffer.filter(m => m.timestamp >= cutoff);

    const averages = recentMetrics.reduce((acc, metric) => {
      if (!acc[metric.name]) {
        acc[metric.name] = { sum: 0, count: 0 };
      }
      acc[metric.name].sum += metric.value;
      acc[metric.name].count += 1;
      return acc;
    }, {} as Record<string, { sum: number; count: number }>);

    const avgResults = Object.entries(averages).reduce((acc, [name, data]) => {
      acc[name] = data.sum / data.count;
      return acc;
    }, {} as Record<string, number>);

    const slowest = recentMetrics
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    return {
      metrics: recentMetrics,
      averages: avgResults,
      slowest,
    };
  }

  private processErrorEvent(error: ErrorEvent): void {
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Monitoring Service - Error:', error);
    }

    // Check for critical errors that need immediate attention
    if (this.isCriticalError(error)) {
      this.triggerAlert('critical_error', {
        message: error.message,
        stack: error.stack,
        context: error.context,
      });
    }
  }

  private isCriticalError(error: ErrorEvent): boolean {
    const criticalPatterns = [
      /database.*connection.*failed/i,
      /authentication.*failed/i,
      /referent.*context.*error/i,
      /rls.*policy.*violation/i,
      /supabase.*error/i,
    ];

    return criticalPatterns.some(pattern => pattern.test(error.message));
  }

  private checkPerformanceThresholds(metric: PerformanceMetric): void {
    const thresholds = {
      'page_load': 3000,
      'api_request': 2000,
      'database_query': 1000,
      'component_render': 100,
      'referent_form_load': 2000,
      'context_setting': 500,
      'data_fetch': 1500,
    };

    const threshold = thresholds[metric.name as keyof typeof thresholds];
    if (threshold && metric.value > threshold) {
      this.triggerAlert('performance_threshold', {
        metric: metric.name,
        value: metric.value,
        threshold,
        unit: metric.unit,
      });
    }
  }

  /**
   * Track referent-specific performance metrics
   */
  trackReferentPerformance(metric: Partial<PerformanceMetric> & {
    referentId?: string;
    campaignId?: string;
    step?: string;
  }): void {
    this.trackPerformance({
      ...metric,
      context: {
        ...metric.context,
        category: 'referent_system',
        referentId: metric.referentId,
        campaignId: metric.campaignId,
        step: metric.step,
      },
    });
  }

  /**
   * Track component render performance
   */
  trackComponentPerformance(componentName: string, renderTime: number, context?: Record<string, any>): void {
    this.trackPerformance({
      name: `component_render_${componentName}`,
      value: renderTime,
      unit: 'ms',
      context: {
        ...context,
        category: 'component_performance',
        component: componentName,
      },
    });
  }

  /**
   * Track API request performance
   */
  trackAPIPerformance(endpoint: string, method: string, responseTime: number, statusCode: number, context?: Record<string, any>): void {
    this.trackPerformance({
      name: 'api_request',
      value: responseTime,
      unit: 'ms',
      context: {
        ...context,
        category: 'api_performance',
        endpoint,
        method,
        statusCode,
      },
    });

    // Track slow API requests
    if (responseTime > 2000) {
      this.trackError({
        level: 'warning',
        message: `Slow API request: ${method} ${endpoint}`,
        context: {
          category: 'performance',
          endpoint,
          method,
          responseTime,
          statusCode,
        },
      });
    }
  }

  /**
   * Get performance insights and recommendations
   */
  getPerformanceInsights(timeWindow: number = 3600000): {
    slowestOperations: Array<{ name: string; avgTime: number; count: number }>;
    performanceTrends: Array<{ hour: string; avgResponseTime: number; count: number }>;
    recommendations: string[];
    criticalIssues: Array<{ issue: string; severity: string; description: string }>;
  } {
    const cutoff = new Date(Date.now() - timeWindow);
    const recentMetrics = this.performanceBuffer.filter(m => m.timestamp >= cutoff);

    // Slowest operations
    const operationMap = new Map<string, { totalTime: number; count: number }>();
    recentMetrics.forEach(metric => {
      if (!operationMap.has(metric.name)) {
        operationMap.set(metric.name, { totalTime: 0, count: 0 });
      }
      const opData = operationMap.get(metric.name)!;
      opData.totalTime += metric.value;
      opData.count++;
    });

    const slowestOperations = Array.from(operationMap.entries())
      .map(([name, data]) => ({
        name,
        avgTime: data.totalTime / data.count,
        count: data.count,
      }))
      .sort((a, b) => b.avgTime - a.avgTime)
      .slice(0, 10);

    // Performance trends by hour
    const hourlyMap = new Map<string, { totalTime: number; count: number }>();
    recentMetrics.forEach(metric => {
      const hour = metric.timestamp.toISOString().substring(0, 13);
      if (!hourlyMap.has(hour)) {
        hourlyMap.set(hour, { totalTime: 0, count: 0 });
      }
      const hourData = hourlyMap.get(hour)!;
      hourData.totalTime += metric.value;
      hourData.count++;
    });

    const performanceTrends = Array.from(hourlyMap.entries())
      .map(([hour, data]) => ({
        hour,
        avgResponseTime: data.totalTime / data.count,
        count: data.count,
      }))
      .sort((a, b) => a.hour.localeCompare(b.hour));

    // Generate recommendations
    const recommendations: string[] = [];
    const criticalIssues: Array<{ issue: string; severity: string; description: string }> = [];

    // Check for consistently slow operations
    slowestOperations.forEach(op => {
      if (op.avgTime > 2000 && op.count > 5) {
        recommendations.push(`Optimize ${op.name} - average time: ${op.avgTime.toFixed(0)}ms`);
        criticalIssues.push({
          issue: `Slow ${op.name}`,
          severity: 'high',
          description: `Operation consistently takes ${op.avgTime.toFixed(0)}ms on average`,
        });
      }
    });

    // Check for performance degradation
    if (performanceTrends.length >= 2) {
      const recent = performanceTrends.slice(-2);
      const [prev, current] = recent;
      if (current.avgResponseTime > prev.avgResponseTime * 1.5) {
        recommendations.push('Performance degradation detected - investigate recent changes');
        criticalIssues.push({
          issue: 'Performance Degradation',
          severity: 'medium',
          description: `Response times increased by ${((current.avgResponseTime / prev.avgResponseTime - 1) * 100).toFixed(1)}%`,
        });
      }
    }

    return {
      slowestOperations,
      performanceTrends,
      recommendations,
      criticalIssues,
    };
  }

  private triggerAlert(type: string, data: any): void {
    // In a real implementation, this would send alerts to external services
    console.warn(`ALERT [${type}]:`, data);
    
    // Store alert for dashboard display
    this.trackError({
      level: 'warning',
      message: `Alert triggered: ${type}`,
      context: { alertType: type, alertData: data },
    });

    // Send alert to external monitoring services
    this.sendExternalAlert(type, data);
  }

  private async sendExternalAlert(type: string, data: any): Promise<void> {
    try {
      // Send to webhook endpoint for external monitoring
      const alertPayload = {
        alertType: type,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        data,
        severity: this.getAlertSeverity(type),
        environment: process.env.NODE_ENV || 'development',
      };

      // In production, this would send to services like Sentry, DataDog, etc.
      if (process.env.NODE_ENV === 'production') {
        await fetch('/api/alerts', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(alertPayload),
        }).catch(error => {
          console.error('Failed to send external alert:', error);
        });
      }
    } catch (_error) {
      console.error('Error sending external alert:', _error);
    }
  }

  private getAlertSeverity(type: string): 'low' | 'medium' | 'high' | 'critical' {
    const severityMap: Record<string, 'low' | 'medium' | 'high' | 'critical'> = {
      'critical_error': 'critical',
      'performance_threshold': 'medium',
      'health_check_failure': 'high',
      'memory_leak': 'high',
      'network_failure': 'high',
      'auth_failure': 'critical',
      'database_error': 'critical',
    };

    return severityMap[type] || 'medium';
  }

  private startHealthChecks(): void {
    // Run health checks every 30 seconds
    setInterval(() => {
      this.runHealthChecks();
    }, 30000);

    // Run initial health check
    setTimeout(() => this.runHealthChecks(), 1000);
  }

  private async runHealthChecks(): Promise<void> {
    // Database health check
    await this.checkDatabaseHealth();
    
    // API health check
    await this.checkAPIHealth();
    
    // Frontend health check
    this.checkFrontendHealth();
  }

  private async checkDatabaseHealth(): Promise<void> {
    const startTime = performance.now();
    
    try {
      // Simple database connectivity check
      const response = await fetch('/api/health/database', {
        method: 'GET',
        timeout: 5000,
      } as any);
      
      const responseTime = performance.now() - startTime;
      
      if (response.ok) {
        this.recordHealthCheck({
          service: 'database',
          status: responseTime > 2000 ? 'degraded' : 'healthy',
          responseTime,
        });
      } else {
        this.recordHealthCheck({
          service: 'database',
          status: 'unhealthy',
          responseTime,
          error: `HTTP ${response.status}`,
        });
      }
    } catch (_error) {
      this.recordHealthCheck({
        service: 'database',
        status: 'unhealthy',
        responseTime: performance.now() - startTime,
        error: _error instanceof Error ? _error.message : 'Unknown error',
      });
    }
  }

  private async checkAPIHealth(): Promise<void> {
    const startTime = performance.now();
    
    try {
      const response = await fetch('/api/health', {
        method: 'GET',
        timeout: 5000,
      } as any);
      
      const responseTime = performance.now() - startTime;
      
      if (response.ok) {
        this.recordHealthCheck({
          service: 'api',
          status: responseTime > 1000 ? 'degraded' : 'healthy',
          responseTime,
        });
      } else {
        this.recordHealthCheck({
          service: 'api',
          status: 'unhealthy',
          responseTime,
          error: `HTTP ${response.status}`,
        });
      }
    } catch (_error) {
      this.recordHealthCheck({
        service: 'api',
        status: 'unhealthy',
        responseTime: performance.now() - startTime,
        error: _error instanceof Error ? _error.message : 'Unknown error',
      });
    }
  }

  private checkFrontendHealth(): void {
    const memoryInfo = (performance as any).memory;
    const connectionInfo = (navigator as any).connection;
    
    let status: 'healthy' | 'degraded' | 'unhealthy' = 'healthy';
    const details: Record<string, any> = {};
    
    // Check memory usage
    if (memoryInfo) {
      const memoryUsage = memoryInfo.usedJSHeapSize / memoryInfo.totalJSHeapSize;
      details.memoryUsage = memoryUsage;
      
      if (memoryUsage > 0.9) {
        status = 'unhealthy';
      } else if (memoryUsage > 0.7) {
        status = 'degraded';
      }
    }
    
    // Check connection quality
    if (connectionInfo) {
      details.connectionType = connectionInfo.effectiveType;
      details.downlink = connectionInfo.downlink;
      
      if (connectionInfo.effectiveType === 'slow-2g' || connectionInfo.downlink < 0.5) {
        status = status === 'unhealthy' ? 'unhealthy' : 'degraded';
      }
    }
    
    // Check error rate
    const recentErrors = this.errorBuffer.filter(e => 
      e.timestamp.getTime() > Date.now() - 300000 // Last 5 minutes
    );
    
    details.recentErrorCount = recentErrors.length;
    
    if (recentErrors.length > 10) {
      status = 'unhealthy';
    } else if (recentErrors.length > 5) {
      status = status === 'unhealthy' ? 'unhealthy' : 'degraded';
    }
    
    this.recordHealthCheck({
      service: 'frontend',
      status,
      details,
    });
  }

  private getCurrentUserId(): string | undefined {
    // This would integrate with your auth system
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.id;
    } catch {
      return undefined;
    }
  }

  private generateId(): string {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Enable or disable monitoring
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Clear all monitoring data
   */
  clear(): void {
    this.errorBuffer = [];
    this.performanceBuffer = [];
    this.healthChecks.clear();
  }

  /**
   * Track referent-specific errors with enhanced context
   */
  trackReferentError(error: Partial<ErrorEvent> & {
    referentId?: string;
    campaignId?: string;
    token?: string;
    step?: string;
  }): void {
    this.trackError({
      ...error,
      context: {
        ...error.context,
        category: 'referent_system',
        referentId: error.referentId,
        campaignId: error.campaignId,
        token: error.token ? `${error.token.substring(0, 8)}...` : undefined,
        step: error.step,
      },
    });
  }

  /**
   * Track authentication errors
   */
  trackAuthError(error: Partial<ErrorEvent> & {
    authMethod?: string;
    userId?: string;
  }): void {
    this.trackError({
      ...error,
      level: 'error',
      context: {
        ...error.context,
        category: 'authentication',
        authMethod: error.authMethod,
        userId: error.userId,
      },
    });

    // Trigger critical alert for auth failures
    this.triggerAlert('auth_failure', {
      message: error.message,
      authMethod: error.authMethod,
      userId: error.userId,
    });
  }

  /**
   * Track database errors
   */
  trackDatabaseError(error: Partial<ErrorEvent> & {
    query?: string;
    table?: string;
    operation?: string;
  }): void {
    this.trackError({
      ...error,
      level: 'error',
      context: {
        ...error.context,
        category: 'database',
        query: error.query,
        table: error.table,
        operation: error.operation,
      },
    });

    // Trigger critical alert for database errors
    this.triggerAlert('database_error', {
      message: error.message,
      query: error.query,
      table: error.table,
      operation: error.operation,
    });
  }

  /**
   * Track network errors
   */
  trackNetworkError(error: Partial<ErrorEvent> & {
    url?: string;
    method?: string;
    statusCode?: number;
    responseTime?: number;
  }): void {
    this.trackError({
      ...error,
      level: 'error',
      context: {
        ...error.context,
        category: 'network',
        url: error.url,
        method: error.method,
        statusCode: error.statusCode,
        responseTime: error.responseTime,
      },
    });

    // Trigger alert for network failures
    if (error.statusCode && error.statusCode >= 500) {
      this.triggerAlert('network_failure', {
        message: error.message,
        url: error.url,
        statusCode: error.statusCode,
        responseTime: error.responseTime,
      });
    }
  }

  /**
   * Get error trends and patterns
   */
  getErrorTrends(timeWindow: number = 86400000): {
    hourlyTrends: Array<{ hour: string; count: number; level: Record<string, number> }>;
    categoryBreakdown: Record<string, number>;
    topErrorMessages: Array<{ message: string; count: number; lastSeen: Date }>;
    criticalErrors: ErrorEvent[];
  } {
    const cutoff = new Date(Date.now() - timeWindow);
    const recentErrors = this.errorBuffer.filter(e => e.timestamp >= cutoff);

    // Hourly trends
    const hourlyMap = new Map<string, { count: number; level: Record<string, number> }>();
    recentErrors.forEach(error => {
      const hour = error.timestamp.toISOString().substring(0, 13);
      if (!hourlyMap.has(hour)) {
        hourlyMap.set(hour, { count: 0, level: {} });
      }
      const hourData = hourlyMap.get(hour)!;
      hourData.count++;
      hourData.level[error.level] = (hourData.level[error.level] || 0) + 1;
    });

    const hourlyTrends = Array.from(hourlyMap.entries())
      .map(([hour, data]) => ({ hour, ...data }))
      .sort((a, b) => a.hour.localeCompare(b.hour));

    // Category breakdown
    const categoryBreakdown = recentErrors.reduce((acc, error) => {
      const category = error.context?.category || 'unknown';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Top error messages
    const messageMap = new Map<string, { count: number; lastSeen: Date }>();
    recentErrors.forEach(error => {
      if (!messageMap.has(error.message)) {
        messageMap.set(error.message, { count: 0, lastSeen: error.timestamp });
      }
      const messageData = messageMap.get(error.message)!;
      messageData.count++;
      if (error.timestamp > messageData.lastSeen) {
        messageData.lastSeen = error.timestamp;
      }
    });

    const topErrorMessages = Array.from(messageMap.entries())
      .map(([message, data]) => ({ message, ...data }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Critical errors
    const criticalErrors = recentErrors.filter(error => 
      error.level === 'error' && 
      (error.context?.category === 'authentication' || 
       error.context?.category === 'database' ||
       this.isCriticalError(error))
    );

    return {
      hourlyTrends,
      categoryBreakdown,
      topErrorMessages,
      criticalErrors,
    };
  }

  /**
   * Export monitoring data for analysis
   */
  exportData(): {
    errors: ErrorEvent[];
    performance: PerformanceMetric[];
    health: HealthCheckResult[];
    session: string;
    timestamp: Date;
    trends: ReturnType<typeof this.getErrorTrends>;
  } {
    return {
      errors: [...this.errorBuffer],
      performance: [...this.performanceBuffer],
      health: Array.from(this.healthChecks.values()),
      session: this.sessionId,
      timestamp: new Date(),
      trends: this.getErrorTrends(),
    };
  }
}

// Singleton instance - désactivé en développement pour éviter les erreurs
export const monitoringService = import.meta.env.DEV 
  ? {
      trackError: () => {},
      trackReferentError: () => {},
      trackAuthError: () => {},
      trackDatabaseError: () => {},
      trackNetworkError: () => {},
      trackPerformance: () => {},
      getHealthStatus: () => ({ overall: 'healthy', services: [], lastUpdated: new Date() }),
      getErrorStats: () => ({ total: 0, recent: [], byCategory: {} }),
      getPerformanceStats: () => ({ averageResponseTime: 0, slowestOperations: [], recentMetrics: [] }),
      getErrorTrends: () => ({ hourly: [], daily: [] }),
      getPerformanceInsights: () => ({ slowestOperations: [], recommendations: [] })
    }
  : new MonitoringService();

// Convenience functions
export const trackError = (error: Partial<ErrorEvent>) => monitoringService.trackError(error);
export const trackReferentError = (error: Parameters<typeof monitoringService.trackReferentError>[0]) => monitoringService.trackReferentError(error);
export const trackAuthError = (error: Parameters<typeof monitoringService.trackAuthError>[0]) => monitoringService.trackAuthError(error);
export const trackDatabaseError = (error: Parameters<typeof monitoringService.trackDatabaseError>[0]) => monitoringService.trackDatabaseError(error);
export const trackNetworkError = (error: Parameters<typeof monitoringService.trackNetworkError>[0]) => monitoringService.trackNetworkError(error);
export const trackPerformance = (metric: Partial<PerformanceMetric>) => monitoringService.trackPerformance(metric);
export const getHealthStatus = () => monitoringService.getHealthStatus();
export const getErrorStats = (timeWindow?: number) => monitoringService.getErrorStats(timeWindow);
export const getPerformanceStats = (timeWindow?: number) => monitoringService.getPerformanceStats(timeWindow);
export const getErrorTrends = (timeWindow?: number) => monitoringService.getErrorTrends(timeWindow);